import { useEffect } from "react";

import { useCoachOptimizationStore } from "@/client/stores/coach-optimization";

import { useCoachScoreReturnable } from "./use-coach-score-return";

/**
 * Ask the browser to confirm before this tab is closed mid-optimization.
 *
 * WHAT IS ACTUALLY AT RISK. The résumé edits themselves are safe — the store autosaves on a 500ms
 * debounce. What is lost is the HANDOFF: the `COACH_RESUME_READY` message that `Save and Continue`
 * posts back to the Career Coach tab is the only thing that attaches this résumé to the job and
 * queues the rescore. Close this tab first and the student has an optimized résumé that is not
 * attached to anything, with their job score unchanged and no indication why.
 *
 * ⚠️ THE BROWSER WILL NOT SHOW OUR TEXT. Every current browser replaced custom `beforeunload`
 * strings with its own generic wording years ago, to stop the dialog being used for scareware. This
 * handler can therefore only produce the prompt; the explanation has to live in the app, which is
 * what CoachUnloadWarning's modal is for. The two ship together for that reason.
 *
 * Gated on `useCoachScoreReturnable`, so a tab with no recipient neither prompts nor warns — there is
 * no handoff left to protect, and `CoachConfirmBar` covers that case by pointing the student at the
 * résumé picker instead.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */
export const useCoachUnloadGuard = () => {
  const session = useCoachOptimizationStore((state) => state.session);
  const confirmed = useCoachOptimizationStore((state) => state.confirmed);
  const returnable = useCoachScoreReturnable();

  const shouldGuard = Boolean(session) && !confirmed && returnable;

  useEffect(() => {
    if (!shouldGuard) return;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Still required by older browsers to trigger the prompt at all; the string itself is ignored.
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [shouldGuard]);

  return shouldGuard;
};
